export const toUint8Array = (value) => {
    if (!value) return null;
    if (value instanceof Uint8Array) return value;
    if (value instanceof Uint8ClampedArray) return new Uint8Array(value);
    if (Array.isArray(value)) return new Uint8Array(value);
    try {
        return new Uint8Array(value);
    } catch (_error) {
        return null;
    }
};

export const normalizeRelationFileType = (value) => {
    const raw = String(value || '').trim();
    if (!raw) {
        return 'jpeg';
    }

    if (raw.includes('/')) {
        return raw.split('/').pop() || 'jpeg';
    }

    return raw.replace(/^\./, '') || 'jpeg';
};

/**
 * 解析图片源的**真实编码格式**（MIME），用于让「登记类型」与「实际字节」保持一致。
 *
 * 背景（2026-10-23）：裁剪后的图片由 AnalysisProcessor 统一 `canvas.toDataURL('image/webp')`
 * 编码，但 store 曾直接把原图 MIME 抄给 processed ⇒ 上传 file_type、R2 资源后缀、
 * 下载文件名后缀、图片代理 data URL 的 mime 全部与实际字节不符。
 *
 * 判定顺序：
 * 1. Blob / File：其 `type` 就是编码结论；
 * 2. data URL：前缀 `data:<mime>` 是浏览器编码器的真实输出
 *    （不支持 WebP 编码的浏览器会静默回退 `image/png`，这里也能如实读到）；
 * 3. 其余（blob: / http(s): / 短枚举）：无法从源推断，回落到 fallback。
 *
 * @param {string|Blob} source 图片源
 * @param {string} [fallback] 无法推断时使用的类型，可传 MIME 或短枚举（jpg / webp）
 * @returns {string|null} 归一化后的 `image/<subtype>`，无法判定返回 null
 */
export const resolveImageMimeType = (source, fallback = '') => {
    if (source && typeof source === 'object' && typeof source.type === 'string' && source.type) {
        return source.type.trim().toLowerCase();
    }

    const value = String(source || '').trim();
    const dataUrlMatch = /^data:([^;,]+)/i.exec(value);
    if (dataUrlMatch && dataUrlMatch[1]) {
        return dataUrlMatch[1].trim().toLowerCase();
    }

    const normalizedFallback = String(fallback || '').trim();
    if (!normalizedFallback) {
        return null;
    }

    return normalizedFallback.includes('/')
        ? normalizedFallback.toLowerCase()
        : `image/${normalizeRelationFileType(normalizedFallback)}`;
};

export const createEmptyFinalResultImageState = () => ({
    scopeKey: '',
    imageData: null,
    hashId: null,
    type: '',
    fileName: '',
    updatedAt: null,
    colorPalette: null,
    beforePalette: null
});

export const createDefaultAnalysisBootstrapState = () => ({
    status: 'idle', // idle | analyzing | crop_deciding | ready_for_execution | error
    originalReady: false,
    cropDecisionReady: false,
    croppingDetected: false,
    processedReady: false,
    readyForExecution: false,
    aiEffectiveSourceKind: 'original',
    aiEffectiveSourceHashId: null,
    borderInfo: null,
    errorCode: null,
    updatedAt: null
});

export const deriveAnalysisBootstrapState = (state, context = {}) => {
    const nextState = {
        ...state
    };

    const requiresProcessedReady = nextState.cropDecisionReady && nextState.croppingDetected;
    const readyForExecution = Boolean(
        nextState.originalReady
        && nextState.cropDecisionReady
        && (!requiresProcessedReady || nextState.processedReady)
    );

    const aiEffectiveSourceKind = readyForExecution && nextState.croppingDetected && nextState.processedReady
        ? 'processed'
        : 'original';

    const originalHashId = String(context?.originalHashId || '').trim() || null;
    const processedHashId = String(context?.processedHashId || '').trim() || null;
    const aiEffectiveSourceHashId = aiEffectiveSourceKind === 'processed'
        ? (processedHashId || null)
        : (originalHashId || null);

    const status = nextState.status === 'error'
        ? 'error'
        : (readyForExecution ? 'ready_for_execution' : nextState.status);

    return {
        ...nextState,
        readyForExecution,
        aiEffectiveSourceKind,
        aiEffectiveSourceHashId,
        status,
        updatedAt: new Date().toISOString()
    };
};

export const normalizeSerializablePalette = (palette) => {
    if (!palette || typeof palette !== 'object') {
        return null;
    }

    try {
        return JSON.parse(JSON.stringify(palette));
    } catch (_error) {
        return null;
    }
};
